// SNIGJ - Cash Register Management Page
// Virtual cash register by payment method and structure
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Wallet, CreditCard, Banknote, Clock, CheckCircle, XCircle, AlertTriangle,
  Plus, Search, Filter, Download, RefreshCw, DollarSign, Calendar,
  Building2, User, FileText, TrendingUp, TrendingDown, BarChart3,
  Receipt, Calculator, Ban, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Payment methods
const PAYMENT_METHODS = [
  { value: 'CASH', label: { fr: 'Espèces', en: 'Cash' }, icon: Banknote, color: 'bg-green-500' },
  { value: 'BANK_TRANSFER', label: { fr: 'Virement bancaire', en: 'Bank Transfer' }, icon: Building2, color: 'bg-blue-500' },
  { value: 'MOBILE_MONEY', label: { fr: 'Mobile Money', en: 'Mobile Money' }, icon: Smartphone, color: 'bg-orange-500' },
  { value: 'CARD', label: { fr: 'Carte bancaire', en: 'Bank Card' }, icon: CreditCard, color: 'bg-purple-500' },
  { value: 'CHECK', label: { fr: 'Chèque', en: 'Check' }, icon: FileText, color: 'bg-cyan-500' }
];

// Payment statuses
const PAYMENT_STATUSES = {
  PENDING: { label: { fr: 'En attente', en: 'Pending' }, color: 'bg-yellow-500', icon: Clock },
  PAID: { label: { fr: 'Payé', en: 'Paid' }, color: 'bg-green-500', icon: CheckCircle },
  OVERDUE: { label: { fr: 'En retard', en: 'Overdue' }, color: 'bg-red-500', icon: AlertTriangle },
  CANCELLED: { label: { fr: 'Annulé', en: 'Cancelled' }, color: 'bg-gray-500', icon: XCircle },
  PARTIAL: { label: { fr: 'Partiel', en: 'Partial' }, color: 'bg-blue-500', icon: TrendingUp }
};

const CashRegisterPage = () => {
  const { user, language } = useAuth();
  const { displayAmount, formatCurrency, preferredCurrency } = useCurrency();
  
  const [transactions, setTransactions] = useState([]);
  const [myCashRegister, setMyCashRegister] = useState(null);
  const [summary, setSummary] = useState({
    total_billed: 0,
    total_collected: 0,
    total_pending: 0,
    total_overdue: 0,
    collection_rate: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Citizen search autocomplete state
  const [citizenSearch, setCitizenSearch] = useState('');
  const [citizenResults, setCitizenResults] = useState([]);
  const [searchingCitizens, setSearchingCitizens] = useState(false);
  const [selectedCitizen, setSelectedCitizen] = useState(null);
  const [citizenCases, setCitizenCases] = useState([]);
  const [showCitizenDropdown, setShowCitizenDropdown] = useState(false);

  // New transaction form
  const [newTransaction, setNewTransaction] = useState({
    citizen_id: '',
    citizen_name: '',
    case_id: '',
    description: '',
    amount: '',
    due_date: '',
    fee_type: 'COURT_FEE'
  });

  // Collection form
  const [collectionForm, setCollectionForm] = useState({
    payment_method: 'CASH',
    amount_paid: '',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadCashRegisterData();
  }, []);

  // Citizen search with debounce
  useEffect(() => {
    const searchCitizens = async () => {
      if (citizenSearch.length < 2) {
        setCitizenResults([]);
        setShowCitizenDropdown(false);
        return;
      }
      
      setSearchingCitizens(true);
      try {
        const response = await api.get(`/cash-register/search-citizens?q=${encodeURIComponent(citizenSearch)}`);
        setCitizenResults(response.data.citizens || []);
        setShowCitizenDropdown(true);
      } catch (error) {
        console.error('Search error:', error);
        setCitizenResults([]);
      } finally {
        setSearchingCitizens(false);
      }
    };
    
    const timer = setTimeout(searchCitizens, 300);
    return () => clearTimeout(timer);
  }, [citizenSearch]);

  const handleSelectCitizen = async (citizen) => {
    setSelectedCitizen(citizen);
    setCitizenSearch(citizen.name);
    setShowCitizenDropdown(false);
    setNewTransaction(prev => ({
      ...prev,
      citizen_id: citizen.user_id,
      citizen_name: citizen.name
    }));
    
    // Load citizen's cases
    try {
      const response = await api.get(`/cash-register/user-cases/${citizen.user_id}`);
      setCitizenCases(response.data.cases || []);
    } catch (error) {
      console.error('Failed to load cases:', error);
      setCitizenCases([]);
    }
  };

  const handleSelectCase = (caseItem) => {
    setNewTransaction(prev => ({
      ...prev,
      case_id: caseItem.case_id,
      description: `${caseItem.case_number || caseItem.case_id} - ${caseItem.title || language === 'fr' ? 'Frais de dossier' : 'Case fees'}`
    }));
  };

  const resetCitizenSearch = () => {
    setCitizenSearch('');
    setSelectedCitizen(null);
    setCitizenCases([]);
    setCitizenResults([]);
    setNewTransaction(prev => ({
      ...prev,
      citizen_id: '',
      citizen_name: '',
      case_id: '',
      description: ''
    }));
  };

  const loadCashRegisterData = async () => {
    try {
      setLoading(true);
      const [transRes, summaryRes] = await Promise.all([
        api.get('/cash-register/transactions'),
        api.get('/cash-register/summary')
      ]);
      setTransactions(transRes.data.transactions || []);
      setSummary(transRes.data.summary || summaryRes.data || {
        total_billed: 0,
        total_collected: 0,
        total_pending: 0,
        total_overdue: 0,
        collection_rate: 0
      });
      setMyCashRegister(transRes.data.cash_register || null);
    } catch (error) {
      console.error('Failed to load cash register:', error);
      // Use mock data for demo
      setTransactions([]);
      setSummary({
        total_billed: 15000,
        total_collected: 12500,
        total_pending: 2000,
        total_overdue: 500,
        collection_rate: 83.3
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransaction = async () => {
    try {
      if (!newTransaction.citizen_name || !newTransaction.amount || !newTransaction.description) {
        toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
        return;
      }
      
      await api.post('/cash-register/transactions', {
        ...newTransaction,
        amount: parseFloat(newTransaction.amount),
        billed_by: user.user_id,
        structure: user.jurisdiction || 'KINSHASA'
      });
      
      toast.success(language === 'fr' ? 'Facture créée' : 'Invoice created');
      setShowCreateModal(false);
      setNewTransaction({
        citizen_id: '',
        citizen_name: '',
        case_id: '',
        description: '',
        amount: '',
        due_date: '',
        fee_type: 'COURT_FEE'
      });
      loadCashRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleCollectPayment = async () => {
    try {
      if (!collectionForm.amount_paid) {
        toast.error(language === 'fr' ? 'Montant requis' : 'Amount required');
        return;
      }
      
      await api.post(`/cash-register/transactions/${selectedTransaction.transaction_id}/collect`, {
        ...collectionForm,
        amount_paid: parseFloat(collectionForm.amount_paid),
        collected_by: user.user_id
      });
      
      toast.success(language === 'fr' ? 'Paiement enregistré' : 'Payment recorded');
      setShowCollectModal(false);
      setSelectedTransaction(null);
      setCollectionForm({
        payment_method: 'CASH',
        amount_paid: '',
        reference: '',
        notes: ''
      });
      loadCashRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openCollectModal = (transaction) => {
    setSelectedTransaction(transaction);
    setCollectionForm({
      payment_method: 'CASH',
      amount_paid: (transaction.amount - (transaction.amount_paid || 0)).toString(),
      reference: '',
      notes: ''
    });
    setShowCollectModal(true);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = !searchQuery || 
      tx.citizen_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tx.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tx.transaction_id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || tx.status === filterStatus;
    const matchesMethod = filterMethod === 'all' || tx.payment_method === filterMethod;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  // Calculate days overdue
  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // Calculate penalty (2% per week overdue, capped at 30%)
  const calculatePenalty = (amount, dueDate) => {
    const daysOverdue = getDaysOverdue(dueDate);
    const weeksOverdue = Math.floor(daysOverdue / 7);
    const penaltyRate = Math.min(0.30, 0.02 * weeksOverdue); // Cap at 30%
    return amount * penaltyRate;
  };

  // Get penalty rate as percentage
  const getPenaltyRate = (dueDate) => {
    const daysOverdue = getDaysOverdue(dueDate);
    const weeksOverdue = Math.floor(daysOverdue / 7);
    return Math.min(30, 2 * weeksOverdue); // Cap at 30%
  };

  const getStatusInfo = (status) => PAYMENT_STATUSES[status] || PAYMENT_STATUSES.PENDING;
  const getMethodInfo = (method) => PAYMENT_METHODS.find(m => m.value === method) || PAYMENT_METHODS[0];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cash-register-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Wallet className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Gestion de Caisse' : 'Cash Register Management'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? `${user?.jurisdiction || 'KINSHASA'} - Vos encaissements et facturations`
                : `${user?.jurisdiction || 'KINSHASA'} - Your collections and billing`}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={loadCashRegisterData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              {language === 'fr' ? 'Nouvelle Facture' : 'New Invoice'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Total Facturé' : 'Total Billed'}</p>
                  <p className="text-xl font-bold">{displayAmount(summary.total_billed, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Encaissé' : 'Collected'}</p>
                  <p className="text-xl font-bold text-green-600">{displayAmount(summary.total_collected, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'En Attente' : 'Pending'}</p>
                  <p className="text-xl font-bold text-yellow-600">{displayAmount(summary.total_pending, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Impayés/Retard' : 'Overdue'}</p>
                  <p className="text-xl font-bold text-red-600">{displayAmount(summary.total_overdue, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Taux Recouvrement' : 'Collection Rate'}</p>
                  <p className="text-xl font-bold text-purple-600">{summary.collection_rate?.toFixed(1) || 0}%</p>
                </div>
              </div>
              <Progress value={summary.collection_rate || 0} className="mt-2 h-2" />
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="all" className="gap-2">
                <Receipt className="w-4 h-4" />
                {language === 'fr' ? 'Toutes' : 'All'}
              </TabsTrigger>
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="w-4 h-4" />
                {language === 'fr' ? 'En attente' : 'Pending'}
              </TabsTrigger>
              <TabsTrigger value="overdue" className="gap-2">
                <AlertTriangle className="w-4 h-4" />
                {language === 'fr' ? 'Impayés' : 'Overdue'}
              </TabsTrigger>
              <TabsTrigger value="collected" className="gap-2">
                <CheckCircle className="w-4 h-4" />
                {language === 'fr' ? 'Encaissés' : 'Collected'}
              </TabsTrigger>
            </TabsList>

            {/* Filters */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-[200px]"
                />
              </div>
              <Select value={filterMethod} onValueChange={setFilterMethod}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={language === 'fr' ? 'Mode paiement' : 'Payment method'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous modes' : 'All methods'}</SelectItem>
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label[language]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Transactions List */}
          <TabsContent value="all" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Transactions' : 'Transactions'}</CardTitle>
                <CardDescription>
                  {filteredTransactions.length} {language === 'fr' ? 'transaction(s)' : 'transaction(s)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3,4,5].map(i => <div key={i} className="h-20 skeleton rounded" />)}
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune transaction' : 'No transactions'}</p>
                    <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Créer une facture' : 'Create invoice'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTransactions.map((tx) => {
                      const statusInfo = getStatusInfo(tx.status);
                      const methodInfo = getMethodInfo(tx.payment_method);
                      const daysOverdue = getDaysOverdue(tx.due_date);
                      const penalty = tx.status === 'OVERDUE' ? calculatePenalty(tx.amount, tx.due_date) : 0;
                      const remaining = tx.amount - (tx.amount_paid || 0);
                      
                      return (
                        <div 
                          key={tx.transaction_id}
                          className={`p-4 rounded-lg border ${tx.status === 'OVERDUE' ? 'border-red-500 bg-red-500/5' : 'border-border'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-muted-foreground">
                                  #{tx.transaction_id?.slice(-8) || 'N/A'}
                                </span>
                                <Badge className={`${statusInfo.color} text-white text-xs`}>
                                  {statusInfo.label[language]}
                                </Badge>
                                {tx.payment_method && (
                                  <Badge variant="outline" className="text-xs gap-1">
                                    <methodInfo.icon className="w-3 h-3" />
                                    {methodInfo.label[language]}
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-4 mt-2">
                                <div>
                                  <p className="font-medium">{tx.citizen_name || 'N/A'}</p>
                                  <p className="text-sm text-muted-foreground">{tx.description}</p>
                                </div>
                              </div>
                              
                              {tx.status === 'OVERDUE' && daysOverdue > 0 && (
                                <div className="mt-2 p-2 bg-red-500/10 rounded text-sm">
                                  <p className="text-red-600 font-medium flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    {daysOverdue} {language === 'fr' ? 'jours de retard' : 'days overdue'}
                                    <span className="ml-2 text-xs">
                                      ({Math.floor(daysOverdue / 7)} {language === 'fr' ? 'semaines' : 'weeks'})
                                    </span>
                                  </p>
                                  {penalty > 0 && (
                                    <p className="text-red-500 flex items-center gap-2">
                                      <span>{language === 'fr' ? 'Pénalité' : 'Penalty'} ({getPenaltyRate(tx.due_date)}%):</span>
                                      <span className="font-semibold">+{displayAmount(penalty, 'USD')}</span>
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="text-right space-y-2">
                              <p className="text-lg font-bold">{displayAmount(tx.amount, 'USD')}</p>
                              {tx.amount_paid > 0 && (
                                <p className="text-sm text-green-600">
                                  {language === 'fr' ? 'Payé:' : 'Paid:'} {displayAmount(tx.amount_paid, 'USD')}
                                </p>
                              )}
                              {remaining > 0 && tx.status !== 'PAID' && (
                                <p className="text-sm text-yellow-600">
                                  {language === 'fr' ? 'Reste:' : 'Remaining:'} {displayAmount(remaining + penalty, 'USD')}
                                </p>
                              )}
                              {tx.status !== 'PAID' && tx.status !== 'CANCELLED' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => openCollectModal(tx)}
                                  className="gap-1"
                                >
                                  <Banknote className="w-4 h-4" />
                                  {language === 'fr' ? 'Encaisser' : 'Collect'}
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {tx.due_date ? new Date(tx.due_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {language === 'fr' ? 'Facturé par:' : 'Billed by:'} {tx.billed_by_name || user?.name}
                            </span>
                            {tx.case_id && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {tx.case_id}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'En Attente de Paiement' : 'Pending Payment'}</CardTitle>
                <CardDescription>
                  {transactions.filter(t => t.status === 'PENDING').length} {language === 'fr' ? 'transaction(s)' : 'transaction(s)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.filter(t => t.status === 'PENDING').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune transaction en attente' : 'No pending transactions'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.filter(t => t.status === 'PENDING').map((tx) => {
                      const remaining = tx.amount - (tx.amount_paid || 0);
                      return (
                        <div key={tx.transaction_id} className="p-4 rounded-lg border">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge className="bg-yellow-500 text-white">{language === 'fr' ? 'En attente' : 'Pending'}</Badge>
                              <p className="font-medium mt-2">{tx.citizen_name}</p>
                              <p className="text-sm text-muted-foreground">{tx.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold">{displayAmount(tx.amount, 'USD')}</p>
                              <Button size="sm" onClick={() => openCollectModal(tx)} className="mt-2">
                                <Banknote className="w-4 h-4 mr-1" />
                                {language === 'fr' ? 'Encaisser' : 'Collect'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="overdue">
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">{language === 'fr' ? 'Impayés / En Retard' : 'Overdue'}</CardTitle>
                <CardDescription>
                  {transactions.filter(t => t.status === 'OVERDUE').length} {language === 'fr' ? 'transaction(s)' : 'transaction(s)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.filter(t => t.status === 'OVERDUE').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50 text-green-500" />
                    <p>{language === 'fr' ? 'Aucun impayé - Excellent!' : 'No overdue - Excellent!'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.filter(t => t.status === 'OVERDUE').map((tx) => {
                      const daysOverdue = getDaysOverdue(tx.due_date);
                      const penalty = calculatePenalty(tx.amount - (tx.amount_paid || 0), tx.due_date);
                      const remaining = tx.amount - (tx.amount_paid || 0);
                      return (
                        <div key={tx.transaction_id} className="p-4 rounded-lg border border-red-500 bg-red-500/5">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge className="bg-red-500 text-white">{language === 'fr' ? 'En retard' : 'Overdue'}</Badge>
                              <p className="font-medium mt-2">{tx.citizen_name}</p>
                              <p className="text-sm text-muted-foreground">{tx.description}</p>
                              <div className="mt-2 p-2 bg-red-500/10 rounded text-sm">
                                <p className="text-red-600 font-medium">
                                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                                  {daysOverdue} {language === 'fr' ? 'jours de retard' : 'days overdue'}
                                </p>
                                {penalty > 0 && (
                                  <p className="text-red-500">
                                    {language === 'fr' ? 'Pénalité' : 'Penalty'} ({getPenaltyRate(tx.due_date)}%): +{displayAmount(penalty, 'USD')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold">{displayAmount(tx.amount, 'USD')}</p>
                              <p className="text-sm text-red-600">
                                {language === 'fr' ? 'Total dû:' : 'Total due:'} {displayAmount(remaining + penalty, 'USD')}
                              </p>
                              <Button size="sm" onClick={() => openCollectModal(tx)} className="mt-2">
                                <Banknote className="w-4 h-4 mr-1" />
                                {language === 'fr' ? 'Encaisser' : 'Collect'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="collected">
            <Card>
              <CardHeader>
                <CardTitle className="text-green-600">{language === 'fr' ? 'Paiements Encaissés' : 'Collected Payments'}</CardTitle>
                <CardDescription>
                  {transactions.filter(t => t.status === 'PAID').length} {language === 'fr' ? 'transaction(s)' : 'transaction(s)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.filter(t => t.status === 'PAID').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucun paiement encaissé' : 'No collected payments'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.filter(t => t.status === 'PAID').map((tx) => {
                      const methodInfo = getMethodInfo(tx.payment_method);
                      return (
                        <div key={tx.transaction_id} className="p-4 rounded-lg border border-green-500 bg-green-500/5">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-green-500 text-white">{language === 'fr' ? 'Payé' : 'Paid'}</Badge>
                                {tx.payment_method && (
                                  <Badge variant="outline" className="text-xs gap-1">
                                    <methodInfo.icon className="w-3 h-3" />
                                    {methodInfo.label[language]}
                                  </Badge>
                                )}
                              </div>
                              <p className="font-medium mt-2">{tx.citizen_name}</p>
                              <p className="text-sm text-muted-foreground">{tx.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">{displayAmount(tx.amount_paid, 'USD')}</p>
                              <p className="text-xs text-muted-foreground">
                                {tx.collected_at ? new Date(tx.collected_at).toLocaleDateString() : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Transaction Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) resetCitizenSearch(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                {language === 'fr' ? 'Nouvelle Facture' : 'New Invoice'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Citizen Search Autocomplete */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {language === 'fr' ? 'Rechercher le citoyen à facturer *' : 'Search citizen to bill *'}
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    value={citizenSearch}
                    onChange={(e) => { setCitizenSearch(e.target.value); setSelectedCitizen(null); }}
                    placeholder={language === 'fr' ? 'Tapez le nom, email ou ID...' : 'Type name, email or ID...'}
                    className="pl-10"
                    data-testid="citizen-search-input"
                  />
                  {searchingCitizens && (
                    <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                  
                  {/* Dropdown Results */}
                  {showCitizenDropdown && citizenResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {citizenResults.map((citizen) => (
                        <div
                          key={citizen.user_id}
                          className="p-3 hover:bg-accent cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectCitizen(citizen)}
                          data-testid={`citizen-option-${citizen.user_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{citizen.name}</p>
                              <p className="text-sm text-muted-foreground">{citizen.email}</p>
                            </div>
                            <Badge variant="outline">{citizen.role}</Badge>
                          </div>
                          {citizen.cases?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {citizen.cases.length} {language === 'fr' ? 'dossier(s)' : 'case(s)'}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {showCitizenDropdown && citizenSearch.length >= 2 && citizenResults.length === 0 && !searchingCitizens && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                      {language === 'fr' ? 'Aucun résultat' : 'No results'}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Citizen Info & Cases */}
              {selectedCitizen && (
                <Card className="bg-accent/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{selectedCitizen.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedCitizen.email}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={resetCitizenSearch}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Citizen's Cases */}
                    {citizenCases.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm">{language === 'fr' ? 'Dossiers associés:' : 'Associated cases:'}</Label>
                        <div className="grid gap-2 max-h-40 overflow-y-auto">
                          {citizenCases.map((caseItem) => (
                            <div
                              key={caseItem.case_id}
                              className={`p-2 rounded border cursor-pointer hover:bg-primary/10 transition-colors ${
                                newTransaction.case_id === caseItem.case_id ? 'border-primary bg-primary/5' : ''
                              }`}
                              onClick={() => handleSelectCase(caseItem)}
                              data-testid={`case-option-${caseItem.case_id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-mono text-sm">{caseItem.case_number || caseItem.case_id}</p>
                                  <p className="text-xs text-muted-foreground truncate">{caseItem.title}</p>
                                </div>
                                <Badge variant="outline" className="text-xs">{caseItem.status}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {citizenCases.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        {language === 'fr' ? 'Aucun dossier associé' : 'No associated cases'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Amount and Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Montant (USD) *' : 'Amount (USD) *'}</Label>
                  <Input 
                    type="number"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})}
                    placeholder="100.00"
                    data-testid="invoice-amount"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Date limite' : 'Due date'}</Label>
                  <Input 
                    type="date"
                    value={newTransaction.due_date}
                    onChange={(e) => setNewTransaction({...newTransaction, due_date: e.target.value})}
                    data-testid="invoice-due-date"
                  />
                </div>
              </div>
              
              {/* Fee Type */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type de frais' : 'Fee type'}</Label>
                <Select 
                  value={newTransaction.fee_type}
                  onValueChange={(v) => setNewTransaction({...newTransaction, fee_type: v})}
                >
                  <SelectTrigger data-testid="fee-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COURT_FEE">{language === 'fr' ? 'Frais de justice' : 'Court fee'}</SelectItem>
                    <SelectItem value="FINE">{language === 'fr' ? 'Amende' : 'Fine'}</SelectItem>
                    <SelectItem value="BAIL">{language === 'fr' ? 'Caution' : 'Bail'}</SelectItem>
                    <SelectItem value="DOCUMENT_FEE">{language === 'fr' ? 'Frais de document' : 'Document fee'}</SelectItem>
                    <SelectItem value="PENALTY">{language === 'fr' ? 'Pénalité' : 'Penalty'}</SelectItem>
                    <SelectItem value="OTHER">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Description *' : 'Description *'}</Label>
                <Textarea 
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
                  placeholder={language === 'fr' ? 'Description de la facturation...' : 'Billing description...'}
                  rows={2}
                  data-testid="invoice-description"
                />
              </div>
              
              {/* Manual Case ID if no case selected */}
              {!newTransaction.case_id && (
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'N° Dossier (optionnel)' : 'Case number (optional)'}</Label>
                  <Input 
                    value={newTransaction.case_id}
                    onChange={(e) => setNewTransaction({...newTransaction, case_id: e.target.value})}
                    placeholder="CASE-2026-001"
                    data-testid="manual-case-id"
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateModal(false); resetCitizenSearch(); }}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleCreateTransaction} 
                className="gap-2"
                disabled={!newTransaction.citizen_name || !newTransaction.amount || !newTransaction.description}
              >
                <Plus className="w-4 h-4" />
                {language === 'fr' ? 'Créer la facture' : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Collect Payment Modal */}
        <Dialog open={showCollectModal} onOpenChange={setShowCollectModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                {language === 'fr' ? 'Encaisser un paiement' : 'Collect Payment'}
              </DialogTitle>
            </DialogHeader>

            {selectedTransaction && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="font-medium">{selectedTransaction.citizen_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedTransaction.description}</p>
                  <p className="text-lg font-bold mt-2">
                    {language === 'fr' ? 'À payer:' : 'To pay:'} {displayAmount(selectedTransaction.amount - (selectedTransaction.amount_paid || 0), 'USD')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Mode de paiement' : 'Payment method'}</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {PAYMENT_METHODS.slice(0, 3).map(method => (
                      <Button
                        key={method.value}
                        type="button"
                        variant={collectionForm.payment_method === method.value ? 'default' : 'outline'}
                        className="gap-2 h-auto py-3"
                        onClick={() => setCollectionForm({...collectionForm, payment_method: method.value})}
                      >
                        <method.icon className="w-4 h-4" />
                        <span className="text-xs">{method.label[language]}</span>
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Montant reçu *' : 'Amount received *'}</Label>
                  <Input 
                    type="number"
                    value={collectionForm.amount_paid}
                    onChange={(e) => setCollectionForm({...collectionForm, amount_paid: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Référence (optionnel)' : 'Reference (optional)'}</Label>
                  <Input 
                    value={collectionForm.reference}
                    onChange={(e) => setCollectionForm({...collectionForm, reference: e.target.value})}
                    placeholder={language === 'fr' ? 'N° transaction, reçu...' : 'Transaction #, receipt...'}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCollectModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleCollectPayment} className="gap-2 bg-green-600 hover:bg-green-700">
                <CheckCircle className="w-4 h-4" />
                {language === 'fr' ? 'Confirmer l\'encaissement' : 'Confirm Collection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CashRegisterPage;
